"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

export default function LearnerSection() {
  // State for counter
  const [count, setCount] = useState(0);
  const [countDisplay, setCountDisplay] = useState("0");
  const targetCount = 6.5; // Target count in millions
  const countDuration = 3000; // Duration in milliseconds
  const countRef = useRef(null);
  const animationStarted = useRef(false);

  // Array of learner images
  const learnerImages = Array.from({ length: 20 }, (_, i) => 
    `https://d31bwppm8yl9g2.cloudfront.net/learner/website/Learne-${i + 1}.svg`
  );
  
  const sectionRef = useRef(null);
  
  // Effect for counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !animationStarted.current) {
          animationStarted.current = true;
          
          const startTime = Date.now();
          const updateCount = () => {
            const elapsedTime = Date.now() - startTime;
            const progress = Math.min(elapsedTime / countDuration, 1);
            
            // Easing function to start fast and slow down (easeOutQuart)
            const easedProgress = 1 - Math.pow(1 - progress, 4);
            
            const currentCount = easedProgress * targetCount;
            setCount(currentCount);
            
            // Format the count with one decimal place
            if (currentCount < targetCount) {
              // During animation, show more decimal places at the beginning, fewer at the end
              const decimalPlaces = Math.max(1, 3 - Math.floor(currentCount));
              setCountDisplay(currentCount.toFixed(decimalPlaces));
              requestAnimationFrame(updateCount);
            } else {
              // Final value
              setCountDisplay(targetCount.toFixed(1));
            }
          };
          
          requestAnimationFrame(updateCount);
        }
      },
      { threshold: 0.25 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [targetCount, countDuration]);

  return (
    <div className="w-[90%] md:w-[80%] mx-auto flex justify-between items-start gap-5 md:flex-row flex-col md:mb-10" ref={sectionRef}>
      {/* Left Side: Heading and Marquee */}
      <div className="md:w-[70%] w-full py-6 md:py-8 bg-[#fcf8ef] rounded-3xl border border-[#ccd4d4] flex flex-col items-center justify-center"
           style={{
             backgroundImage: "url('https://d31bwppm8yl9g2.cloudfront.net/learner/website/dots.svg')",
             backgroundRepeat: "repeat",
             backgroundSize: "auto"
           }}>
        {/* Heading */}
        <div className="text-left mb-12 w-full md:text-left">
          <h2 className="text-[32px] font-medium text-[#002726] mx-8">
            <span ref={countRef}>{countDisplay}</span>M+ Learners
          </h2>
          <p className="text-base font-normal text-gray-600 mx-8">have reaped benefits from our programs</p>
        </div>

        {/* Marquee - Using inline styles for the animation */}
        <div className="overflow-hidden w-full relative">
          <div className="flex whitespace-nowrap" 
               style={{
                 animation: "marquee 30s linear infinite",
               }}>
            {/* First set of images */}
            {learnerImages.map((src, index) => (
              <div key={`first-${index}`} className="inline-block px-2.5">
                <div className="relative h-[100px] w-[80px]">
                  <Image 
                    src={src}
                    alt={`Learner ${index + 1}`}
                    className="rounded-lg shadow-sm "
                    width={100}
                    height={100}
                  />
                </div>
              </div>
            ))}
            
            {/* Duplicate set for seamless looping */}
            {learnerImages.map((src, index) => (
              <div key={`second-${index}`} className="inline-block px-2.5">
                <div className="relative h-[100px] w-[100px]">
                  <Image 
                    src={src}
                    alt={`Learner ${index + 1}`}
                    className="rounded-lg shadow-sm object-contain"
                    width={100}
                    height={100}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side: Lottie Animation */}
      <div className="md:w-[30%] w-full  bg-white rounded-3xl border border-[#ccd4d4]">
        <div className="w-full h-[245px] md:h-[285px] relative flex items-center justify-center">
          <DotLottieReact
            src="/images/GS-Brands-updated.lottie"
            loop
            autoplay
            className="h-auto w-full object-cover"
          />
          
        </div>
      </div>
    </div>
  );
}